import React, { useEffect, useState, useRef } from 'react';

export interface BackToStoriesButtonProps {
  isOpen: boolean;
  goBack: () => void;
  label?: string;
}

// Floating button overlay that lets events pass through everywhere but the button
const BackToStoriesButton: React.FC<BackToStoriesButtonProps> = ({ isOpen, goBack, label = 'Back to Stories' }) => {
  const [flashOn, setFlashOn] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    // trigger an immediate flash
    setFlashOn(true);
    timeoutRef.current = window.setTimeout(() => setFlashOn(false), 1000);

    // then repeat every 10 seconds
    intervalRef.current = window.setInterval(() => {
      setFlashOn(true);
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => setFlashOn(false), 1000);
    }, 10000);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setFlashOn(false);
    };
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 2000,
      }}
    >
      <style>
        {`
          @keyframes sheen-slide {
            0% { transform: translateX(-130%) skewX(20deg); opacity: 0.0; }
            0% { opacity: 0.65; }
            100% { transform: translateX(230%) skewX(20deg); opacity: 0.0; }
          }
          .btn-with-sheen { position: relative; overflow: hidden; }
          .btn-with-sheen .sheen {
            position: absolute;
            top: 0; bottom: 0;
            left: 0;
            width: 60%;
            background: linear-gradient(90deg,
              rgba(0, 0, 0) 0%,
              rgba(0, 0, 0.6) 50%,
              rgba(0, 0, 0) 100%);
            filter: saturate(100%) blur(10px);
            animation: sheen-slide 1s ease-out forwards;
          }
          .flash-glow {
            box-shadow:
              0 0 0 2px rgba(0, 0, 0,0.6),
              0 0 12px rgba(0, 0, 0,0.45),
              0 4px 10px rgba(0,0,0,0.12);
          }
        `}
      </style>
      <button
        onClick={goBack}
        style={{
          position: 'absolute',
          right: 10,
          bottom: 10,
          pointerEvents: 'auto',
          padding: '7.5px 10px',
          borderRadius: 6,
          border: flashOn ? '1px solid rgba(0, 0, 0, 0.75)' : '1px solid #333',
          background: '#ffffff85',
          color: '#333',
          fontSize: 14,
          boxShadow: flashOn ? '0 0 0 1px rgba(0, 0, 0, 0.75), 0 1px 3px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.15)',
          cursor: 'pointer',
        }}
        className={flashOn ? 'btn-with-sheen flash-glow' : 'btn-with-sheen'}
        aria-label="Go back to stories"
      >
        {flashOn && <div className="sheen" />}
        {label}
      </button>
    </div>
  );
};

export default BackToStoriesButton;

